import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthProvider";
import {
  addCustomTagToParticipantAsync,
  addStandardTagToParticipantAsync,
  editParticipant,
  getParticipantTagsAsync,
  getParticipantTagOptionsAsync,
  getCountryCodes,
  getParticipantInfo,
  getWaivers,
  removeTagFromParticipantAsync,
  updateParticipantDescriptionAsync,
  ParticipantTagAssignmentResponseDto,
} from "../../../services/participantServices/ParticipantService";
import {
  GetParticipantSnapshotAsync,
  ParticipantSnapshotDto,
} from "../../../services/participantSnapshotService/ParticipantSnapshotService";
import { toast } from "react-toastify";
import AccordionComponent from "../../ui/Accordion/AccordionComponent";
import Select, { SingleValue, StylesConfig } from "react-select";
import Loader from "../loader/Loader";
import "./infotab.css";
import { GetLastProfileChangeStatusAsync } from "../../../services/EmployeeService/EmployeeService";
import { AxiosError } from "axios";
import { CountryCodeOption, ParticipantDTO, WaiverOption } from "../../../interfaces/Participant";

const initialNewParticipant: ParticipantDTO = {};

const customSelectStyles: StylesConfig<
  CountryCodeOption | WaiverOption,
  false
> = {
  control: (provided, state) => ({
    ...provided,
    display: "flex",
    padding: "0.625rem 0.875rem",
    alignItems: "center",
    borderRadius: "0.625rem",
    border: "1px solid #C9C9C9",
    background: "#FFF",
    boxShadow: "0 2px 0 0 rgba(231, 235, 238, 0.20) inset",
    minHeight: "auto",
    height: "auto",
    "&:hover": {
      borderColor: "#C9C9C9",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0",
    display: "flex",
    alignItems: "center",
  }),
  input: (provided) => ({
    ...provided,
    color: "#4A5568",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: "Inter",
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "1.25rem",
    letterSpacing: "-0.00963rem",
    margin: "0",
    padding: "0",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#4A5568",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: "Inter",
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "1.25rem",
    letterSpacing: "-0.00963rem",
    margin: "0",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#4A5568",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: "Inter",
    fontSize: "1rem",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "1.25rem",
    letterSpacing: "-0.00963rem",
    margin: "0",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "0 0.5rem 0 0",
    color: "#000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      color: "#000",
    },
    svg: {
      display: "none",
    },
  }),
};

const Infotab: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [participant, setParticipant] = useState<any>();
  const [authToken, setAuthToken] = useState<string>("");
  const { id: participantId } = useParams();
  const [participantLoading, setParticipantLoading] = useState(false);
  const [waivers, setWaivers] = useState<WaiverOption[]>([]);
  const [countryCodeOptions, setCountryCodeOptions] = useState<
    CountryCodeOption[]
  >([]);
  const [newParticipant, setNewParticipant] = useState<ParticipantDTO>(
    initialNewParticipant
  );
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [snapshot, setSnapshot] = useState<ParticipantSnapshotDto | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const [standardTags, setStandardTags] = useState<
    { id: number; name: string }[]
  >([]);
  const [allTags, setAllTags] = useState<
    { id: number; name: string; custom: boolean }[]
  >([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagActionLoadingById, setTagActionLoadingById] = useState<
    Record<number, boolean>
  >({});
  const [tagAddLoading, setTagAddLoading] = useState(false);
  const [tagError, setTagError] = useState<string>("");
  const [tagInput, setTagInput] = useState<string>("");

  const [descriptionDraft, setDescriptionDraft] = useState<string>("");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [tags, setTags] = useState<ParticipantTagAssignmentResponseDto[]>([]);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    participantSsnLast4?: string;
    cpocDate?: string;
    cpocEndDate?: string;
    certificationDate?: string;
  }>({});

  // Accordion states
  const [participantNotesOpen, setParticipantNotesOpen] = useState(true);
  const [participantTagsOpen, setParticipantTagsOpen] = useState(true);
  const [personalDetailsOpen, setPersonalDetailsOpen] = useState(true);

  const [isParticipantUpdateRequestPending, setIsParticipantUpdateRequestPending] =
    useState<boolean>(false);


  const participantTags = useMemo(() => {
    const tags = participant?.tags ?? newParticipant.tags ?? [];
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    return [];
  }, [participant]);

  const normalizedParticipantTags = useMemo(() => {
    return participantTags
      .map((t: any) => {
        if (!t) return null;
        if (typeof t === "string") {
          return {
            id: undefined as unknown as number,
            name: t,
            isCustom: true,
          };
        }

        const id =
          typeof t.id === "number"
            ? t.id
            : typeof t.tagId === "number"
            ? t.tagId
            : undefined;
        const name = String(t.name ?? t.tagName ?? "").trim();
        const isCustom =
          typeof t.isCustom === "boolean"
            ? t.isCustom
            : typeof t.custom === "boolean"
            ? t.custom
            : undefined;

        if (!name) return null;
        return { id, name, isCustom };
      })
      .filter(Boolean);
  }, [participantTags]);

  const participantTagNames = useMemo(() => {
    return new Set(
      (normalizedParticipantTags as any[]).map((t: any) =>
        String(t.name).toLowerCase()
      )
    );
  }, [normalizedParticipantTags]);

  useEffect(() => {
    currentUser?.getIdToken().then((token) => {
      if (token) {
        setAuthToken(token);
        getParticipant(token);
      }
    });
    GetLastProfileChangeStatus();
  }, [participantId, currentUser]);

  useEffect(() => {
    const fetchSnapshot = async () => {
      if (!currentUser || !participantId) return;
      const numericId = Number(participantId);
      if (!numericId || Number.isNaN(numericId)) return;

      try {
        setSnapshotLoading(true);
        const token = await currentUser.getIdToken();
        const res = await GetParticipantSnapshotAsync(token, numericId);
        if (res.statusCode === 200 || res.statusCode === 201) {
          setSnapshot(res.data);
        } else {
          setSnapshot(null);
        }
      } catch {
        setSnapshot(null);
      } finally {
        setSnapshotLoading(false);
      }
    };

    fetchSnapshot();
  }, [currentUser, participantId]);

  useEffect(() => {
    const fetchInfo = async () => {
      if (currentUser) {
        const authToken: string = await currentUser.getIdToken();
        const waiversFetched = await getWaivers(authToken);

        if (waiversFetched.success) {
          setWaivers(
            waiversFetched.data.map((w: any) => ({
              value: w.id,
              label: w.waiverName,
            }))
          );
        }
      }
    };

    fetchInfo();
  }, [currentUser]);

  useEffect(() => {
    const fetchCodes = async () => {
      try {
        if (!currentUser) return;
        const authToken: string = await currentUser.getIdToken();

        const data = await getCountryCodes(authToken);

        const formattedOptions: CountryCodeOption[] = data.map((item: any) => ({
          value: item.id,
          label: `${item.name} (${item.code})`,
          code: item.code,
        }));

        setCountryCodeOptions(formattedOptions);
      } catch (error) {
        console.error("Error fetching country codes:", error);
      }
    };

    fetchCodes();
  }, [currentUser]);

  const GetLastProfileChangeStatus = async () => {
    if (!currentUser || !participantId) return;
    const token = await currentUser.getIdToken();
    try {
      const res = await GetLastProfileChangeStatusAsync(token, currentUser?.uid, Number(participantId));
      if (res.success) {
        setIsParticipantUpdateRequestPending(res.data);
      } else {
        toast.error(res.message);
      }
    } catch (error: any) {
      toast.error(error instanceof AxiosError ? error.response?.data?.message : error.message || "Unable to get the status");
    }
  };

  // --- Check for Changes Helper ---
  const checkForChanges = (updatedFields: Partial<ParticipantDTO>) => {
    if (!participant) return false;

    // Create a hypothetical full object representing the state *after* this change
    const nextState = { ...newParticipant, ...updatedFields };

    // Helper to normalize values for comparison (treat null/undefined as empty string)
    const normalize = (val: any) => (val === null || val === undefined) ? "" : String(val).trim();
    const normalizeDate = (val: any) => {
      if (!val) return "";
      try { return val.split("T")[0]; } catch { return ""; }
    };

    // Compare fields
    const hasFirstNameChanged = normalize(nextState.firstName) !== normalize(participant.firstName);
    const hasLastNameChanged = normalize(nextState.lastName) !== normalize(participant.lastName);
    const hasEmailChanged = normalize(nextState.email) !== normalize(participant.participantEmail);
    const hasPhoneChanged = normalize(nextState.phoneNumber) !== normalize(participant.mobileNumber);
    const hasWaiverChanged = (nextState.waiverId || 0) !== (participant.waiverTypeId || 0);
    const hasSsnChanged = normalize(nextState.participantSsnLast4) !== normalize(participant.participantSsnLast4);

    const hasCpocDateChanged = normalize(nextState.cpocDate) !== normalizeDate(participant.cpocDate);
    const hasCpocEndDateChanged = normalize(nextState.cpocEndDate) !== normalizeDate(participant.cpocEndDate);
    const hasCertDateChanged = normalize(nextState.certificationDate) !== normalizeDate(participant.certificationDate);

    // Return true if ANY field is different
    return (
      hasFirstNameChanged ||
      hasLastNameChanged ||
      hasEmailChanged ||
      hasPhoneChanged ||
      hasWaiverChanged ||
      hasSsnChanged ||
      hasCpocDateChanged ||
      hasCpocEndDateChanged ||
      hasCertDateChanged
    );
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateName = (name: string): string | undefined => {
    if (!name || name.trim().length < 2) {
      return "Name must be at least 2 characters";
    }
    if (name.trim().length > 30) {
      return "Name must not exceed 30 characters";
    }
    return undefined;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Remove all whitespace for firstName, lastName, and email
    const sanitizedValue =
      name === "firstName" || name === "lastName" || name === "email"
        ? value.replace(/\s/g, "")
        : value;

    // Validation
    let error: string | undefined = undefined;

    if (name === "firstName" || name === "lastName") {
      error = validateName(sanitizedValue);
      setValidationErrors((prev) => ({ ...prev, [name]: error }));
    } else if (name === "email") {
      if (sanitizedValue && !validateEmail(sanitizedValue)) {
        error = "Please enter a valid email address";
      }
      setValidationErrors((prev) => ({ ...prev, [name]: error }));
    } else if (name === "phoneNumber") {
      // Only allow digits, max 10
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length > 10) {
        return; // Don't update if exceeds limit
      }
      setValidationErrors((prev) => ({
        ...prev,
        phoneNumber:
          digitsOnly.length !== 10 && digitsOnly.length > 0
            ? "Phone number must be 10 digits"
            : undefined,
      }));
      setNewParticipant((prev) => ({ ...prev, [name]: digitsOnly } as ParticipantDTO));
      setIsDirty(checkForChanges({ [name]: digitsOnly }));
      return;
    } else if (name === "participantSsnLast4") {
      // Only allow digits, max 4
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length > 4) {
        return; // Don't update if exceeds limit
      }
      setValidationErrors((prev) => ({
        ...prev,
        participantSsnLast4:
          digitsOnly.length !== 4 && digitsOnly.length > 0
            ? "SSN must be exactly 4 digits"
            : undefined,
      }));
      setNewParticipant((prev) => ({ ...prev, [name]: digitsOnly } as ParticipantDTO));
      setIsDirty(checkForChanges({ [name]: digitsOnly }));
      return;
    }

    setNewParticipant((prev) => ({ ...prev, [name]: sanitizedValue } as ParticipantDTO));
    setIsDirty(checkForChanges({ [name]: sanitizedValue }));
  };

  const handleDateChange = (name: string, value: string) => {
    setNewParticipant((prev) => ({ ...prev, [name]: value } as ParticipantDTO));
    setValidationErrors((prev) => ({ ...prev, [name]: undefined }));
    setIsDirty(checkForChanges({ [name]: value }));
  };

  const handleSelectChange =
    (field: keyof ParticipantDTO) => (selectedOption: SingleValue<any>) => {
      const value = selectedOption ? selectedOption.value : null;
      setNewParticipant(
        (prev) => ({ ...prev, [field]: value } as ParticipantDTO)
      );
      setIsDirty(checkForChanges({ [field]: value }));
    };

  const getParticipant = async (token: string) => {
    if (!participantId) return;
    try {
      setParticipantLoading(true);
      const response = await getParticipantInfo(participantId, token);

      if (response === "unauthorized") {
        toast.error("unauthorized");
      } else if (response !== null) {
        setParticipant(response);
      }
    } finally {
      setParticipantLoading(false);
    }
  };

  useEffect(() => {
    refreshParticipantTags();
  }, []);

  useEffect(() => {
    if (participant) {
      // Format dates from ISO string to YYYY-MM-DD for date inputs
      const formatDateForInput = (dateString: string | undefined) => {
        if (!dateString) return "";
        try {
          return dateString.split("T")[0];
        } catch {
          return "";
        }
      };

      setNewParticipant({
        firstName: participant.firstName,
        lastName: participant.lastName,
        email: participant.participantEmail,
        phoneNumber: participant.mobileNumber || "",
        countryCodeId: participant.countryId || null,
        waiverId: participant.waiverTypeId || null,
        certificationCode: participant.certificationCode || "",
        participantSsnLast4: participant.participantSsnLast4,
        tags: tags,
        cpocDate: formatDateForInput(participant.cpocDate),
        cpocEndDate: formatDateForInput(participant.cpocEndDate),
        certificationDate: formatDateForInput(participant.certificationDate),
      });

      setDescriptionDraft(participant?.description || "");
      setIsEditingDescription(false);
      setDescriptionError("");
      setIsEditMode(false);
      setIsDirty(false);
      setValidationErrors({});
    }
  }, [participant]);

  const fetchAllTags = async () => {
    if (!currentUser) return;
    const orgId = participant?.organizationId;
    if (!orgId || typeof orgId !== "number") return;

    try {
      setTagsLoading(true);
      const token = await currentUser.getIdToken();
      const res = await getParticipantTagOptionsAsync(token, orgId);
      if (res.success && Array.isArray(res.data)) {
        const allTagData = res.data.map((t) => ({
          id: t.id,
          name: t.name,
          custom: t.custom,
        }));
        setAllTags(allTagData);
        // Since all tags are custom, set all of them as available
        setStandardTags(allTagData.map((t) => ({ id: t.id, name: t.name })));
      } else {
        setAllTags([]);
        setStandardTags([]);
      }
    } catch {
      setAllTags([]);
      setStandardTags([]);
    } finally {
      setTagsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTags();
  }, [currentUser, participant?.organizationId]);

  const matchingStandardTag = useMemo(() => {
    const input = tagInput.trim().toLowerCase();
    if (!input) return null;
    return standardTags.find((t) => t.name.toLowerCase() === input) || null;
  }, [tagInput, standardTags]);

  const refreshParticipantTags = async () => {
    if (!currentUser || !participantId) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await getParticipantTagsAsync(token, participantId);
      if (res.success && Array.isArray(res.data)) {
        setTags(res.data);
        setParticipant((prev: any) => ({
          ...prev,
          tags: res.data.map((t: any) => ({
            id: t.tagId,
            name: t.tagName,
            isCustom: t.custom,
          })),
        }));
      }
    } catch {
      // best-effort refresh; keep UI state as-is
    }
  };

  const suggestedTags = useMemo(() => {
    const input = tagInput.trim().toLowerCase();
    if (!input) return allTags.slice(0, 8);
    return allTags
      .filter((t) => t.name.toLowerCase().includes(input))
      .slice(0, 8);
  }, [tagInput, allTags]);

  const handleSaveDescription = async () => {
    if (!currentUser || !participantId) return;

    try {
      setDescriptionSaving(true);
      setDescriptionError("");
      const token = await currentUser.getIdToken();
      const res = await updateParticipantDescriptionAsync(
        token,
        participantId,
        descriptionDraft
      );
      if (res.success) {
        setParticipant((prev: any) => ({
          ...prev,
          description: descriptionDraft,
        }));
        setIsEditingDescription(false);
      } else {
        setDescriptionError(res.message || "Failed to update description.");
      }
    } catch {
      setDescriptionError("Failed to update description.");
    } finally {
      setDescriptionSaving(false);
    }
  };

  const handleCancelDescription = () => {
    setDescriptionDraft(participant?.description || "");
    //setDescriptionDraft("");
    setIsEditingDescription(false);
    setDescriptionError("");
  };

  const assignTagFromSuggestion = async (tag: {
    id: number;
    name: string;
    custom: boolean;
  }) => {
    if (!currentUser || !participantId) return;

    const tagName = tag.name;
    if (participantTagNames.has(tagName.toLowerCase())) {
      setTagError("");
      return;
    }

    try {
      setTagAddLoading(true);
      setTagError("");
      const token = await currentUser.getIdToken();

      // Use assign API for suggested tags (both standard and custom)
      const res = tag.custom
        ? await addCustomTagToParticipantAsync(token, participantId, tagName)
        : await addStandardTagToParticipantAsync(token, participantId, tag.id);

      if (res.success) {
        await refreshParticipantTags();
        // Refresh available tags list after successful addition
        await fetchAllTags();
        setTagInput("");
      } else {
        setTagError(res.message || "Failed to add tag.");
      }
    } catch {
      setTagError("Failed to add tag.");
    } finally {
      setTagAddLoading(false);
    }
  };

  const addTagByName = async (name: string) => {
    if (!currentUser || !participantId) return;
    const trimmed = name.trim();
    if (!trimmed) return;

    if (participantTagNames.has(trimmed.toLowerCase())) {
      setTagInput("");
      setTagError("");
      return;
    }

    try {
      setTagAddLoading(true);
      setTagError("");
      const token = await currentUser.getIdToken();
      const standard = standardTags.find(
        (t) => t.name.toLowerCase() === trimmed.toLowerCase()
      );

      const res = standard
        ? await addStandardTagToParticipantAsync(
            token,
            participantId,
            standard.id
          )
        : await addCustomTagToParticipantAsync(token, participantId, trimmed);

      if (res.success) {
        await refreshParticipantTags();
        // Refresh available tags list after successful addition
        await fetchAllTags();
        setTagInput("");
      } else {
        setTagError(res.message || "Failed to add tag.");
      }
    } catch {
      setTagError("Failed to add tag.");
    } finally {
      setTagAddLoading(false);
    }
  };

  const removeTag = async (tag: any) => {
    if (!currentUser || !participantId) return;
    if (!tag?.id) {
      setTagError("Unable to remove this tag.");
      return;
    }

    try {
      setTagError("");
      setTagActionLoadingById((prev) => ({ ...prev, [tag.id]: true }));
      const token = await currentUser.getIdToken();
      const res = await removeTagFromParticipantAsync(
        token,
        participantId,
        tag.id
      );
      if (res.success) {
        await refreshParticipantTags();
      } else {
        setTagError(res.message || "Failed to remove tag.");
      }
    } catch {
      setTagError("Failed to remove tag.");
    } finally {
      setTagActionLoadingById((prev) => ({ ...prev, [tag.id]: false }));
    }
  };

  const handleSaveParticipant = async () => {
    // Validate before saving
    const errors: typeof validationErrors = {};

    if (newParticipant.firstName) {
      const firstNameError = validateName(newParticipant.firstName);
      if (firstNameError) errors.firstName = firstNameError;
    }

    if (newParticipant.lastName) {
      const lastNameError = validateName(newParticipant.lastName);
      if (lastNameError) errors.lastName = lastNameError;
    }

    if (newParticipant.email && !validateEmail(newParticipant.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (newParticipant.phoneNumber && newParticipant.phoneNumber.length !== 10) {
      errors.phoneNumber = "Phone number must be 10 digits";
    }

    if (newParticipant.participantSsnLast4 && newParticipant.participantSsnLast4.length !== 4) {
      errors.participantSsnLast4 = "SSN must be exactly 4 digits";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error("Please fix validation errors before saving");
      return;
    }

    const updatedData = {
      id: participant?.id || 0,
      participantId: participant?.id || 0,
      firstName:
        (newParticipant.firstName && newParticipant?.firstName.trim()) ||
        participant?.firstName ||
        "",
      lastName:
        (newParticipant?.lastName && newParticipant?.lastName.trim()) ||
        participant?.lastName ||
        "",
      email: newParticipant?.email || participant?.participantEmail || "",
      mobileNumber:
        newParticipant?.phoneNumber || participant?.phoneNumber || "",
      waiverId: newParticipant?.waiverId || participant?.waiverId || 0,
      regionId: newParticipant?.regionId || participant?.regionId || 0,
      countryId: newParticipant?.countryCodeId || participant?.countryId,
      participantSsnLast4:
        newParticipant.participantSsnLast4 || participant?.participantSsnLast4,
      cpocDate: newParticipant.cpocDate || participant?.cpocDate,
      cpocEndDate: newParticipant.cpocEndDate || participant?.cpocEndDate,
      certificationDate: newParticipant.certificationDate || participant?.certificationDate,
    };

    try {
      if (!currentUser) return;
      const authToken: string = await currentUser.getIdToken();
      setLoading(true);
      const response = await editParticipant(updatedData, authToken);

      if (response.success) {
        toast.success(response.message);
        setIsParticipantUpdateRequestPending(true);
        await getParticipant(authToken);
        setIsDirty(false);
        setLoading(false);
        // navigate("/participants");
      } else {
        toast.error(response.message);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error saving participant:", error);
      toast.error("Something went wrong while saving participant.");
      setLoading(false);
    }
  };

  if (loading || participantLoading) {
    return <Loader />;
  }

  return (
    <>

      {/* {!participantLoading && participant ? (
        <div className="mb-6 mt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                participant?.isCompliant
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full mr-2 ${
                  participant?.isCompliant ? "bg-green-400" : "bg-red-400"
                }`}
              ></span>
              {participant?.isCompliant ? "Compliant" : "Non-compliant"}
            </span>

            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path
                  fillRule="evenodd"
                  d="M4 5a2 2 0 012-2 1 1 0 000 2H6a2 2 0 100 4h2a2 2 0 100-4h-.5a1 1 0 000-2H8a2 2 0 012-2h2a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V5z"
                  clipRule="evenodd"
                />
              </svg>
              {snapshotLoading ? "" : snapshot?.cirCount ?? 0} CIR
              {snapshotLoading ? <Spinner size="sm" className="ml-2" /> : null}
            </span>

            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z"
                  clipRule="evenodd"
                />
              </svg>
              {snapshotLoading ? "" : snapshot?.filesNeedingReview ?? 0} Files
              to review
              {snapshotLoading ? <Spinner size="sm" className="ml-2" /> : null}
            </span>
          </div>
        </div>
      ) : null} */}

      <div className="my_accordian ">

        {/* <div className="my_accordian"> */}
        {/* <AccordionComponent
          key="0"
          isOpen={personalDetailsOpen}
          onToggle={() => setPersonalDetailsOpen(!personalDetailsOpen)}
          title="Personal Details"
        > */}
        <div className="ps-info-grid">
          {/* Three columns layout: First Name, Last Name, E-mail */}
          <div className="ps-info-row">
            <div className="ps-info-field">
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={newParticipant?.firstName || ""}
                onChange={handleInputChange}
                placeholder="Enter first name"
                minLength={2}
                maxLength={30}
                className={validationErrors.firstName ? "error" : ""}
              />
              {validationErrors.firstName && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.firstName}
                </span>
              )}
            </div>

            <div className="ps-info-field">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={newParticipant?.lastName || ""}
                onChange={handleInputChange}
                placeholder="Enter last name"
                minLength={2}
                maxLength={30}
                className={validationErrors.lastName ? "error" : ""}
              />
              {validationErrors.lastName && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.lastName}
                </span>
              )}
            </div>

            <div className="ps-info-field">
              <label>E-mail</label>
              <input
                type="email"
                name="email"
                value={newParticipant?.email || participant?.participantEmail || ""}
                onChange={handleInputChange}
                placeholder="email@domain.com"
                style={{ background: "#F4F4F4" }}
                className={validationErrors.email ? "error" : ""}
              />
              {validationErrors.email && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.email}
                </span>
              )}
            </div>
          </div>

          {/* Three columns layout: Phone Number, Waiver, SSN Last 4 Digit */}
          <div className="ps-info-row">
            <div className="ps-info-field">
              <label>Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                value={newParticipant?.phoneNumber || ""}
                onChange={handleInputChange}
                placeholder="Enter phone number (10 digits)"
                maxLength={10}
                className={validationErrors.phoneNumber ? "error" : ""}
              />
              {validationErrors.phoneNumber && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.phoneNumber}
                </span>
              )}
            </div>

            <div className="ps-info-field">
              <label>Waiver</label>
              <Select
                styles={customSelectStyles}
                options={waivers}
                value={
                  waivers.find((w) => w.value === newParticipant?.waiverId) ||
                  null
                }
                onChange={handleSelectChange("waiverId")}
                name="waiverId"
                placeholder="Select Waiver"
                components={{
                  DropdownIndicator: (props) => (
                    <div {...props.innerProps} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',  cursor: 'pointer', height: '100%' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 12 7" fill="none">
                        <path d="M0 0.7L5.66 6.36L11.32 0.7L10.61 0L5.66 4.95L0.71 0L0 0.7Z" fill="black"/>
                      </svg>
                    </div>
                  ),
                }}
              />
            </div>

            <div className="ps-info-field">
              <label>SSN Last 4 Digit</label>
              <input
                type="text"
                maxLength={4}
                name="participantSsnLast4"
                value={newParticipant?.participantSsnLast4 || ""}
                onChange={handleInputChange}
                placeholder="Enter last 4 digits"
                inputMode="numeric"
                pattern="[0-9]{4}"
                className={validationErrors.participantSsnLast4 ? "error" : ""}
              />
              {validationErrors.participantSsnLast4 && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.participantSsnLast4}
                </span>
              )}
            </div>
          </div>

          {/* Three columns layout: CPOC Date, CPOC End Date, Confirmation Date */}
          <div className="ps-info-row">
            <div className="ps-info-field ps-info-field-date">
              <label>CPOC Date</label>
              <input
                type="date"
                name="cpocDate"
                className="ps-date-input"
                value={newParticipant?.cpocDate || participant?.cpocDate?.split("T")[0] || ""}
                onChange={(e) => handleDateChange("cpocDate", e.target.value)}
              />
              {validationErrors.cpocDate && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.cpocDate}
                </span>
              )}
            </div>

            <div className="ps-info-field ps-info-field-date">
              <label>CPOC End Date</label>
              <input
                type="date"
                name="cpocEndDate"
                className="ps-date-input"
                value={newParticipant?.cpocEndDate || participant?.cpocEndDate?.split("T")[0] || ""}
                onChange={(e) => handleDateChange("cpocEndDate", e.target.value)}
              />
              {validationErrors.cpocEndDate && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.cpocEndDate}
                </span>
              )}
            </div>

            <div className="ps-info-field ps-info-field-date">
              <label>Confirmation Date</label>
              <input
                type="date"
                name="certificationDate"
                className="ps-date-input"
                value={newParticipant?.certificationDate || participant?.certificationDate?.split("T")[0] || ""}
                onChange={(e) => handleDateChange("certificationDate", e.target.value)}
              />
              {validationErrors.certificationDate && (
                <span className="error-message" style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem", display: "block" }}>
                  {validationErrors.certificationDate}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="ps-action-buttons !mt-8">
          <button
            className="ps-btn-request-update"
            onClick={handleSaveParticipant}
            disabled={!isDirty || loading}
            type="button"
          >
            {loading ? "Requesting..." : "Request update"}
          </button>
                  {
                      isParticipantUpdateRequestPending &&
                      (
                          <button
                              className="ps-btn-update-pending"
                              type="button"
                              disabled
                          >
                              Update request pending
                          </button>
                      )
                  }
        </div>
        {/* </AccordionComponent> */}
      </div>
    </>
  );
};

export default Infotab;